// Variable bookkeeping for the flow editor. Everything here mirrors what the runtime actually
// does, so the editor never promises a reference the backend won't resolve:
//
//  - `Flows::VariableInterpolator::PATTERN` decides which names are interpolable at all, so a
//    name outside VARIABLE_KEY_PATTERN would silently render as literal `{{name}}` forever.
//  - `Bots::Brains::Flow` interpolates exactly four fields (INTERPOLATED_FIELDS) and reads a
//    bare variable name in exactly four others (NAME_FIELDS). `condition.cases[].equals` is
//    deliberately not interpolated there, so it isn't listed here either.
//
// Variables are keyed by name rather than by id (Typebot's model), which keeps the graph
// readable and the runtime untouched - the cost is that a rename has to rewrite every
// reference, which is what renameVariableInNodes does.

export const VARIABLE_KEY_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

// Fields holding a bare variable name, split by whether the node writes it or reads it -
// the panel shows the difference, the cascades treat both the same.
const NAME_FIELDS = {
  wait_for_input: { variable: 'write' },
  ask_choice: { variable: 'write' },
  set_variable: { key: 'write' },
  condition: { variable: 'read' },
};

// Fields where `{{key}}` gets expanded at runtime. `set_attribute.key` is absent on purpose:
// it names a contact/conversation custom attribute, not a flow variable.
const INTERPOLATED_FIELDS = {
  send_message: ['content'],
  ask_choice: ['prompt'],
  set_variable: ['value'],
  set_attribute: ['value'],
};

const REFERENCE_PATTERN = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

// Escaped because graphs written before names were validated can hold anything the old
// free-text field accepted - an unescaped `(` there would throw instead of just not matching.
const referenceRegex = key =>
  new RegExp(
    `\\{\\{\\s*${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\}\\}`,
    'g'
  );

export const isValidVariableKey = key => VARIABLE_KEY_PATTERN.test(key || '');

export const variableReference = key => ['{{', key, '}}'].join('');

/**
 * Every variable the graph touches, mapped to the nodes that write it and the nodes that read
 * it. Reads cover both the bare-name fields and `{{key}}` inside interpolated text.
 */
export const collectVariableUsage = (nodes = []) => {
  const usage = {};
  const record = (key, kind, nodeId) => {
    if (!key) return;
    usage[key] = usage[key] || { writtenIn: [], readIn: [] };
    const bucket = usage[key][kind === 'write' ? 'writtenIn' : 'readIn'];
    if (!bucket.includes(nodeId)) bucket.push(nodeId);
  };

  nodes.forEach(node => {
    const data = node.data || {};

    Object.entries(NAME_FIELDS[node.type] || {}).forEach(([field, kind]) =>
      record(data[field], kind, node.id)
    );

    (INTERPOLATED_FIELDS[node.type] || []).forEach(field => {
      const text = data[field];
      if (typeof text !== 'string') return;
      Array.from(text.matchAll(REFERENCE_PATTERN)).forEach(match =>
        record(match[1], 'read', node.id)
      );
    });
  });

  return usage;
};

/** Rewrites every reference to `from` so it points at `to`. Mutates the nodes in place. */
export const renameVariableInNodes = (nodes, from, to) => {
  const pattern = referenceRegex(from);
  const replacement = variableReference(to);

  nodes.forEach(node => {
    const data = node.data || {};

    Object.keys(NAME_FIELDS[node.type] || {}).forEach(field => {
      if (data[field] === from) data[field] = to;
    });

    (INTERPOLATED_FIELDS[node.type] || []).forEach(field => {
      if (typeof data[field] !== 'string') return;
      data[field] = data[field].replace(pattern, replacement);
    });
  });
};

/**
 * Drops every reference to `key`: bare-name fields go back to empty, `{{key}}` disappears from
 * the text it was in. Leaving the references behind instead would make the variable reappear in
 * the list the moment it was deleted.
 */
export const clearVariableInNodes = (nodes, key) => {
  const pattern = referenceRegex(key);

  nodes.forEach(node => {
    const data = node.data || {};

    Object.keys(NAME_FIELDS[node.type] || {}).forEach(field => {
      if (data[field] === key) data[field] = '';
    });

    (INTERPOLATED_FIELDS[node.type] || []).forEach(field => {
      if (typeof data[field] !== 'string') return;
      data[field] = data[field].replace(pattern, '');
    });
  });
};
